/**
 * Kreslenie zostavy regálu CRC v Rozpočte (KOLO 237) — náhľad v rozpočte tovaru.
 *
 * Jeden zdroj s konfigurátorom: moduly stránky Regál CRC (`tools/crc_cennik/regal`)
 * sa načítajú z `/regal/` za behu (`import()`, rovnaký pôvod — CSP `script-src
 * 'self'`), cenník CRC je ten istý `vyrobky.json` a štýl kreslenia `vykres.css`.
 * Diel v obrázku nesie `data-kluc` = kľúč riadku kusovníka (`klucRiadku`) =
 * `dielec.regal_kluc` — tak sa riadok a diel nájdu navzájom. Toto je jediné miesto,
 * ktoré na moduly stránky siaha; test ho nahrádza.
 */

const ADRESA = "/regal/";

/** Riadok kusovníka stránky — len čo náhľad potrebuje. */
interface Riadok {
  nazov?: string;
}
interface KusovnikZostavy {
  zadanie: Record<string, unknown>;
  riadky: Riadok[];
}
interface ModulRegal {
  DEKORY: Record<string, string>;
  nacitajCennik(data: unknown): unknown;
  kusovnik(
    zadania: unknown[],
    cennik: unknown,
    dekory: Record<string, string>,
    povrch?: Record<string, string> | null,
  ): { zostavy: KusovnikZostavy[] };
  klucRiadku(riadok: Riadok): string;
}
interface ModulObrazok {
  predvolenyPohlad(zadanie: Record<string, unknown>): Pohlad;
  svgZostavy(
    zostava: KusovnikZostavy,
    volby: { pohlad: Pohlad; dekory: Record<string, string>; id: string; vyskaPx: number; nazvy: Map<string, string> },
  ): { svg: string };
}

/** Dokument regálu, ako ho uložil konfigurátor (`varianta.regal_json.dokument`). */
export interface DokumentRegalu {
  dekory?: Record<string, string>;
  /** Farba kovu a povrch častí celého regálu (KOLO 238) — zostava mení len to, čo má inak. */
  povrch?: Record<string, string>;
  zostavy?: Array<{ uid?: string; meno?: string } & Record<string, unknown>>;
}

export type Pohlad = "zlava" | "sprava" | "spredu" | "zboku" | "rez";

/** Pohľady ako v konfigurátore (`obrazok.js` POHLADY), kratšie názvy do panela. */
export const POHLADY: { id: Pohlad; nazov: string; popis: string }[] = [
  { id: "zlava", nazov: "Zľava", popis: "Zošikma zľava" },
  { id: "sprava", nazov: "Sprava", popis: "Zošikma sprava" },
  { id: "spredu", nazov: "Spredu", popis: "Spredu s kótami" },
  { id: "zboku", nazov: "Zboku", popis: "Zboku s kótami" },
  { id: "rez", nazov: "Zhora", popis: "Rez zhora" },
];

let moduly: Promise<{ regal: ModulRegal; obrazok: ModulObrazok; cennik: unknown }> | null = null;

function nacitaj(): Promise<{ regal: ModulRegal; obrazok: ModulObrazok; cennik: unknown }> {
  if (!moduly) {
    moduly = (async () => {
      const [regal, obrazok, data] = await Promise.all([
        import(/* @vite-ignore */ `${ADRESA}regal.js`) as Promise<ModulRegal>,
        import(/* @vite-ignore */ `${ADRESA}obrazok.js`) as Promise<ModulObrazok>,
        fetch(`${ADRESA}vyrobky.json`, { credentials: "same-origin" }).then((r) => {
          if (!r.ok) throw new Error(`cenník CRC sa nenačítal (${r.status})`);
          return r.json() as Promise<unknown>;
        }),
      ]);
      return { regal, obrazok, cennik: regal.nacitajCennik(data) };
    })().catch((e: unknown) => {
      moduly = null; // ďalší pokus znova (výpadok siete)
      throw e;
    });
  }
  return moduly;
}

/** Štýl kreslenia z konfigurátora — raz do hlavičky dokumentu. */
export function pripojStylKreslenia(): void {
  if (document.getElementById("vykres-css")) return;
  const link = document.createElement("link");
  link.id = "vykres-css";
  link.rel = "stylesheet";
  link.href = `${ADRESA}vykres.css`;
  document.head.append(link);
}

export interface Kresba {
  svg: string;
  pohlad: Pohlad;
}

/** SVG zostavy `uid` regálu — 1 kus, ako v Prehľade dielov konfigurátora. */
export async function nakresliZostavu(
  dokument: DokumentRegalu,
  uid: string,
  pohlad: Pohlad | null,
  vyskaPx: number,
): Promise<Kresba> {
  const { regal, obrazok, cennik } = await nacitaj();
  const zadanie = (dokument.zostavy ?? []).find((z) => z.uid === uid);
  if (!zadanie) throw new Error("zostava v regáli už nie je — otvor Upraviť regál");
  const dekory = { ...regal.DEKORY, ...(dokument.dekory ?? {}) };
  const z = regal.kusovnik([zadanie], cennik, dekory, dokument.povrch ?? null).zostavy[0];
  if (!z) throw new Error("zostava sa nedá nakresliť");
  const p = pohlad ?? obrazok.predvolenyPohlad(z.zadanie);
  const nazvy = new Map(z.riadky.map((r) => [regal.klucRiadku(r), r.nazov ?? ""]));
  const { svg } = obrazok.svgZostavy(z, { pohlad: p, dekory, id: `nahlad-${uid}`, vyskaPx, nazvy });
  return { svg, pohlad: p };
}
